"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";

/**
 * Event name for SSE status changes
 * Components can listen to this event to react to SSE connection status changes
 */
export const SSE_STATUS_EVENT = "sse-status-change";
export const CHAT_TYPING_EVENT = "chat-typing-event";

/**
 * LocalStorage key for disabling SSE
 */
const SSE_DISABLED_KEY = "techxeed-sse-disabled";

/**
 * Check if SSE is disabled via localStorage
 */
export function isSSEDisabled(): boolean {
	if (typeof window === "undefined") return false;
	return localStorage.getItem(SSE_DISABLED_KEY) === "true";
}

/**
 * Enable or disable SSE via localStorage
 */
export function setSSEDisabled(disabled: boolean): void {
	if (typeof window === "undefined") return;
	if (disabled) {
		localStorage.setItem(SSE_DISABLED_KEY, "true");
	} else {
		localStorage.removeItem(SSE_DISABLED_KEY);
	}
	// Dispatch event to notify listeners
	window.dispatchEvent(
		new CustomEvent(SSE_STATUS_EVENT, {
			detail: { enabled: !disabled },
		}),
	);
}

/**
 * Hook to manage SSE connection for real-time message updates
 * 
 * @param userId - The user ID to subscribe to messages for
 * @param enabled - Whether the SSE connection should be active
 * @param onMessage - Callback when a new message is received
 */
export function useMessageSubscription(
	userId: string | undefined,
	enabled: boolean = true,
	onMessage?: (data: any) => void,
) {
	const { data: session } = useSession();
	const [isConnected, setIsConnected] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);
	const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const reconnectAttemptsRef = useRef(0);
	const maxReconnectAttempts = 5;
	const reconnectDelay = 3000; // 3 seconds

	useEffect(() => {
		// Don't connect if disabled, no userId, or no session token
		if (!enabled || !userId || !session?.idToken || isSSEDisabled()) {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
			setIsConnected(false);
			setIsConnecting(false);
			return;
		}

		let isMounted = true;

		const connectSSE = async () => {
			if (!isMounted) return;

			// Clean up any existing connection
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			setIsConnecting(true);
			abortControllerRef.current = new AbortController();

			try {
				const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "";
				const url = `${backendUrl}/chats/subscribe/${userId}`;

				const response = await fetch(url, {
					method: "GET",
					headers: {
						Authorization: `Bearer ${session.idToken}`,
						Accept: "text/event-stream",
					},
					signal: abortControllerRef.current.signal,
				});

				if (!response.ok) {
					throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
				}

				if (!response.body) {
					throw new Error("Response body is null");
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let buffer = "";

				setIsConnected(true);
				setIsConnecting(false);
				reconnectAttemptsRef.current = 0;

				// Dispatch status change event
				window.dispatchEvent(
					new CustomEvent(SSE_STATUS_EVENT, {
						detail: { enabled: true, connected: true },
					}),
				);

				// Read the stream
				while (true) {
					const { done, value } = await reader.read();

					if (done) {
						break;
					}

					if (!isMounted) {
						reader.cancel();
						break;
					}

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split("\n");
					buffer = lines.pop() || ""; // Keep incomplete line in buffer

					for (const line of lines) {
						if (line.startsWith("data: ")) {
							try {
								const data = JSON.parse(line.slice(6));
								
								// Ignore heartbeat messages
								if (data.heartbeat) {
									continue;
								}

								if (data.data?.type === "typing") {
									window.dispatchEvent(
										new CustomEvent(CHAT_TYPING_EVENT, {
											detail: data.data,
										}),
									);
								}

								// Call the onMessage callback if provided
								if (onMessage && data.data) {
									onMessage(data.data);
								}
							} catch (error) {
								console.error("[SSE] Failed to parse message:", error, line);
							}
						}
					}
				}
			} catch (error: any) {
				if (!isMounted) return;

				// Don't log abort errors (they're expected when cleaning up)
				if (error.name !== "AbortError") {
					console.error("[SSE] Connection error:", error);
				}

				setIsConnected(false);
				setIsConnecting(false);

				// Dispatch status change event
				window.dispatchEvent(
					new CustomEvent(SSE_STATUS_EVENT, {
						detail: { enabled: false, connected: false },
					}),
				);

				// Attempt to reconnect if we haven't exceeded max attempts
				if (
					reconnectAttemptsRef.current < maxReconnectAttempts &&
					error.name !== "AbortError"
				) {
					reconnectAttemptsRef.current++;
					reconnectTimeoutRef.current = setTimeout(() => {
						if (isMounted) {
							connectSSE();
						}
					}, reconnectDelay * reconnectAttemptsRef.current); // Exponential backoff
				}
			}
		};

		connectSSE();

		return () => {
			isMounted = false;
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
				reconnectTimeoutRef.current = null;
			}
			setIsConnected(false);
			setIsConnecting(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, userId, session?.idToken]);

	return {
		isConnected,
		isConnecting,
	};
}

